import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { applicationSessionStorage } from '@application/application_storage_session';
import { withTranslation } from '@application/i18n/with_translation';
import { UnitService } from '@application/unit.service';
import { BreakPoints, Button, DialogAlert, Flex, InputTextArea, Text, Tooltip, useBreakpoint } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { useVonageEncryptionWarning, VonageEncryptionWarning } from '@component/vonage_encryption_warning/mod';
import { AppointmentCaseModel, AppointmentData, MetaData } from '@feature/cases/mod';
import { useResolve } from '@visiba/instantiation';
import { forwardRef, HTMLAttributes, MouseEvent, ReactNode, Ref, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

// TODO: Needs other data structures
type MergedData = AppointmentData & MetaData;

interface Props {
	data: MergedData | null;
	model: AppointmentCaseModel;
}

const elements = {
	outerContainer: styled.div`
		width: 100%;
		display: flex;
		flex: 1;
		flex-direction: column;
		justify-content: end;

		${tokens.breakpointQuery.medium} {
			container: appointment-container / inline-size;
			padding-bottom: 0;
		}
	`,
	group: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			border-top: 1px solid ${tokens.color.border.default};
			gap: ${tokens.spacing.x2};
			padding-top: ${tokens.spacing.x2};
		}
	`,
	innerContainer: styled.div`
		display: flex;
		flex-direction: column;
		gap: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			flex-direction: row;
			align-items: flex-start;
			gap: ${tokens.spacing.x2};
			justify-content: space-between;
		}
	`,
	primaryAction: styled.div`
		display: flex;
		flex-direction: column;
		text-align: center;
		gap: ${tokens.spacing.x1};

		@container appointment-container (min-width: 500px) {
			text-align: right;
			align-items: flex-end;
			order: 1;
			flex-shrink: 0;
		}

		@media screen and (max-width: 599px) {
		}
	`,
	cancelAction: styled.div`
		display: flex;
		flex-direction: column;
		border-top: 1px solid ${tokens.color.border.default};
		padding-top: ${tokens.spacing.x2};

		@container appointment-container (min-width: 500px) {
			padding: 0;
			border: none;
			order: 0;
			flex-shrink: 0;
		}
	`,
	fixed: styled(Flex)`
		position: fixed;
		bottom: 0;
		left: 0;
		width: 100%;
		background: ${tokens.color.bg.surface};
		padding: ${tokens.spacing.x2};
		box-shadow: ${tokens.elevation.floating};
	`,
};

const CANCELLATION_REASON_MAX_LENGTH = 4000;

export function InfoViewAppointmentActions({ data, model }: Props): JSX.Element | null {
	const { hasEncryptionSupport } = useVonageEncryptionWarning();
	const isMediumDeviceAndAbove = useBreakpoint(BreakPoints.Medium | BreakPoints.Large);
	const [bottomPadding, setBottomPadding] = useState(0);

	const handleAddBottomPaddingDependingOnFixedHeight = useCallback((node: HTMLElement) => {
		setBottomPadding(node?.getBoundingClientRect().height ?? 0);
	}, []);

	if (data == null || data.completed) return null;

	const showPrimaryAction = data.type === 'video' && data.link != null;

	return (
		<elements.outerContainer style={{ paddingBottom: `${bottomPadding}px` }}>
			{isMediumDeviceAndAbove ? (
				<elements.group>
					{data.vonageEncryptionRequired ? <VonageEncryptionWarning /> : null}
					<elements.innerContainer>
						<PrimaryAction data={data} hasEncryptionSupport={hasEncryptionSupport} />
						<elements.cancelAction>
							<AppointmentActions model={model} data={data} />
							<RebookAction data={data} />
						</elements.cancelAction>
					</elements.innerContainer>
				</elements.group>
			) : (
				<>
					<elements.innerContainer>
						<elements.cancelAction>
							<AppointmentActions model={model} data={data} />
							<RebookAction data={data} />
						</elements.cancelAction>
					</elements.innerContainer>
					{data.vonageEncryptionRequired || showPrimaryAction ? (
						<elements.fixed gap={tokens.spacing.x2} ref={handleAddBottomPaddingDependingOnFixedHeight}>
							{data.vonageEncryptionRequired ? <VonageEncryptionWarning /> : null}
							<PrimaryAction data={data} hasEncryptionSupport={hasEncryptionSupport} />
						</elements.fixed>
					) : null}
				</>
			)}
		</elements.outerContainer>
	);
}
function PrimaryAction({ data, hasEncryptionSupport }: { data: MergedData; hasEncryptionSupport: boolean }) {
	const navigate = useNavigate();
	const callButtonIsDisabled = !data.lobbyIsOpen || (data.vonageEncryptionRequired && !hasEncryptionSupport);

	if (data.type !== 'video' || data.link == null) return null;

	function handleJoinCallButtonClick(): void {
		if (data.link == null) return;

		navigate(data.link);
	}

	return (
		<elements.primaryAction>
			<Button
				size='medium'
				variant='primary'
				icon='video'
				onClick={handleJoinCallButtonClick}
				disabled={callButtonIsDisabled}
				disabledText={withTranslation('PW.Component.Visits.AppointmentInfo.LobbyNotOpenInfo')}
			>
				{withTranslation('PW.Component.Visits.AppointmentInfo.Button.JoinCall')}
			</Button>
			{!data.lobbyIsOpen ? (
				<Text variant='labelSmallRegular' color={tokens.color.content.default}>
					{withTranslation('PW.Component.Visits.AppointmentInfo.LobbyNotOpenInfo')}
				</Text>
			) : null}
		</elements.primaryAction>
	);
}

function RebookAction({ data }: { data: MergedData }): JSX.Element {
	const navigate = useNavigate();
	const analyticCtx = useAnalyticContext();
	const unitService = useResolve(UnitService);
	const [reschedulingReason, setReschedulingReason] = useState('');
	const [isDialogOpen, setIsDialogOpen] = useState(false);

	function handleRebookClick(): void {
		analyticCtx.trackEvent('Clicked rebook appointment button');
		const unit = unitService.getUnitNodeInTreeByReceptionId(data.receptionId);
		const bookingRoute = `/${unit.routeWithoutLicenceholder}/booking/time-slot/digital?rescheduleTicket=${data.ticket}`;

		// Store the rescheduling reason in the sessionStorage so it can be used during the booking flow
		if (reschedulingReason.trim()) {
			applicationSessionStorage.set('reschedulingReason', reschedulingReason);
			navigate(bookingRoute);
			setIsDialogOpen(false);
		} else {
			// Explicitly clear any previously stored rescheduling reason to avoid using stale data
			applicationSessionStorage.removeItem('reschedulingReason');
			navigate(bookingRoute);
			setIsDialogOpen(false);
		}
	}

	if (!data.reschedulable) return <></>;

	const disabledText = withTranslation('Appointment.Description.TooLateToRebook');

	return (
		<>
			<Tooltip content={data.reschedulable ? null : disabledText}>
				<Button
					size='medium'
					variant='ghost'
					disabled={!data.reschedulable}
					disabledText={disabledText}
					icon='calendarClock'
					onClick={() => setIsDialogOpen(true)}
				>
					{withTranslation('Appointment.Button.RescheduleVisit.Title')}
				</Button>
			</Tooltip>
			<DialogAlert
				isOpen={isDialogOpen}
				onOpenChange={setIsDialogOpen}
				header={withTranslation('Appointment.AppointmentInfo.Reschedule.Dialog.Title')}
				description={withTranslation('Appointment.AppointmentInfo.Reschedule.Dialog.Text')}
				abortText={withTranslation('Common.Button.Abort')}
				successText={withTranslation('Appointment.AppointmentInfo.Reschedule.Dialog.Ok.Button')}
				onSuccess={handleRebookClick}
				onAbort={() => setIsDialogOpen(false)}
			>
				<InputTextArea
					label={withTranslation('Appointment.AppointmentInfo.Reschedule.Dialog.Reason.Placeholder')}
					placeholder={withTranslation('Appointment.AppointmentInfo.Reschedule.Dialog.Reason.Placeholder')}
					value={reschedulingReason}
					onChange={(e) => setReschedulingReason(e.target.value)}
					maxLength={1000}
					minHeight={120}
					maxHeight={300}
					autoHeight
				/>
			</DialogAlert>
		</>
	);
}

function AppointmentActions({ model, data }: { model: AppointmentCaseModel; data: MergedData }): JSX.Element {
	const analyticCtx = useAnalyticContext();
	const [cancellationReason, setCancellationReason] = useState('');
	const isCancellationReasonTooLong = cancellationReason.length >= CANCELLATION_REASON_MAX_LENGTH;

	function analyticQuirk_handleModalAlertAbort(): void {
		analyticCtx.trackEvent('Backed out of cancellation dialog');
	}

	function handleCancel(): Promise<void> {
		analyticCtx.trackEvent('Confirmed cancellation of appointment');

		return model.cancelAppointment(cancellationReason);
	}

	if (!data.canCancel) return <></>;

	return (
		<elements.cancelAction>
			{data.canCancel ? (
				<DialogAlert
					header={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Title')}
					description={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Description')}
					abortText={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Abort')}
					successText={withTranslation('PW.Component.Visits.AppointmentInfo.CancelCall.Success')}
					onSuccess={handleCancel}
					onAbort={analyticQuirk_handleModalAlertAbort}
					trigger={
						<DialogAlertTrigger analyticAction='Opened cancel appointment dialog'>
							{withTranslation('PW.Component.Visits.AppointmentInfo.Button.CancelAppointment')}
						</DialogAlertTrigger>
					}
				>
					<InputTextArea
						label={withTranslation('Appointment.AppointmentInfo.Cancel.Dialog.Reason.Placeholder')}
						placeholder={withTranslation('Appointment.AppointmentInfo.Cancel.Dialog.Reason.Placeholder')}
						value={cancellationReason}
						onChange={(e) => setCancellationReason(e.target.value)}
						minHeight={120}
						maxHeight={300}
						autoHeight
						characterCount={CANCELLATION_REASON_MAX_LENGTH}
						invalid={isCancellationReasonTooLong}
						invalidText={withTranslation('Input.Text.MaxLength.Error', {
							maxLength: `${CANCELLATION_REASON_MAX_LENGTH}`,
						})}
						maxLength={CANCELLATION_REASON_MAX_LENGTH}
					/>
				</DialogAlert>
			) : null}
		</elements.cancelAction>
	);
}

interface DialogAlertTriggerProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'aria-label'> {
	children: ReactNode;
	analyticAction: string;
}
const DialogAlertTrigger = forwardRef<HTMLButtonElement, DialogAlertTriggerProps>(
	({ analyticAction, children, onClick, ...rest }, ref) => {
		const analyticCtx = useAnalyticContext();

		function handleClick(event: MouseEvent<HTMLButtonElement>): void {
			if (onClick != null) {
				onClick(event);
			}

			// TODO: Make useAnalyticContext return a function wrapping queueMicrotask,
			// can't have events bloating up the main thread.
			queueMicrotask(() => {
				analyticCtx.trackEvent(analyticAction);
			});
		}

		return (
			<Button ref={ref} size='medium' variant='ghost' onClick={handleClick} icon='calendarX' {...rest}>
				{children}
			</Button>
		);
	},
);
